import type { ManaSymbol, PublicCard, PublicState } from "../protocol";
import { cardLabel, isCreatureCard, isFaceDown, isLandCard, isPlaneswalkerCard, livePlayers, power, toughness } from "./game-text";

// A battlefield as the board lays it out: lands, creatures, planeswalkers and everything else, with
// identical permanents stacked into one pile.

export type PermGroup = { key: string; cards: PublicCard[]; top: PublicCard };
export type GroupedField = { lands: PermGroup[]; creatures: PermGroup[]; planeswalkers: PermGroup[]; others: PermGroup[] };

export function cardsIn(zone: { [oid: string]: PublicCard }): PublicCard[] {
  return Object.values(zone).sort((a, b) => a.object_id - b.object_id);
}

// Auras and Equipment are drawn on what they're attached to, whoever controls it.
export function attachmentsOf(state: PublicState, oid: number): PublicCard[] {
  return Object.values(state.players).flatMap((p) => cardsIn(p.battlefield))
    .filter((c) => c.attached_to.kind === "Some" && c.attached_to.values[0] === oid);
}

// Permanents stack when nothing tells them apart: name, facing, power and toughness, tapped, damage, every counter,
// token, what was chosen for them, a Room's unlocked doors, being saddled or prepared, a Class's level, and summoning sickness.
// `solo` ones never stack: those the board singles out (attacking, blocking, or a choice's
// candidates), commanders, and any with something attached.
export function groupField(state: PublicState, seat: string, solo: Set<number>): GroupedField {
  const out: GroupedField = { lands: [], creatures: [], planeswalkers: [], others: [] };
  const player = state.players[seat];
  if (!player) return out;
  const index = new Map<string, PermGroup>();
  for (const c of cardsIn(player.battlefield)) {
    if (c.attached_to.kind === "Some" && isAttachedToPermanent(state, c)) continue;
    // A land creature (an earthbent land, 701.66a) fights, so it sits with the creatures.
    const bucket = isCreatureCard(c) ? out.creatures : isPlaneswalkerCard(c) ? out.planeswalkers : isLandCard(c) ? out.lands : out.others;
    const alone = solo.has(c.object_id) || c.is_commander || attachmentsOf(state, c.object_id).length > 0;
    const key = alone ? `solo:${c.object_id}` : sameness(state, seat, c);
    const group = index.get(key);
    if (group) { group.cards.push(c); continue; }
    const fresh: PermGroup = { key, cards: [c], top: c };
    index.set(key, fresh);
    bucket.push(fresh);
  }
  return out;
}

// How wide a row's cards may be so the row fits `room` pixels on one line: `widest` while there's
// room, narrower as the battlefield fills. A stacked pile's ghosts stick out a little;
// each attachment exposes 42% of a tile beside its host.
export function fittedCardWidth(groups: PermGroup[], widest: number, room: number, gap: number, fixed: number, attachments = 0): number {
  if (groups.length === 0) return widest;
  const ghosts = groups.reduce((sum, g) => sum + (g.cards.length > 1 ? 8 : 0), 0);
  const fit = (room - fixed - ghosts - gap * (groups.length - 1)) / (groups.length + attachments * 0.42);
  return Math.max(16, Math.min(widest, Math.floor(fit)));
}

const MAX_ROW_LINES = 3;
const WRAP_BELOW = 0.65;

// A row too crowded to keep its usual cards on one line wraps onto more, the fewest that keep the
// cards at least WRAP_BELOW of `widest`, else MAX_ROW_LINES with whatever width then fits.
export function wrappedRowFit(groups: PermGroup[], widest: number, room: number, gap: number, fixed: number, attachments = 0): { width: number; lines: number } {
  let width = fittedCardWidth(groups, widest, room, gap, fixed, attachments);
  for (let lines = 2; lines <= MAX_ROW_LINES && width < widest * WRAP_BELOW; lines++) {
    const perLine = Math.ceil(groups.length / lines);
    const chunk = groups.slice(0, perLine);
    const fit = fittedCardWidth(chunk, widest, room, gap, fixed, Math.ceil(attachments / lines));
    if (fit > width) return { width: fit, lines };
  }
  return { width, lines: 1 };
}

function isAttachedToPermanent(state: PublicState, c: PublicCard): boolean {
  const host = c.attached_to.kind === "Some" ? c.attached_to.values[0] : null;
  return host !== null && Object.values(state.players).some((p) => p.battlefield[String(host)] !== undefined);
}

function sameness(state: PublicState, seat: string, c: PublicCard): string {
  return JSON.stringify([
    cardLabel(c), isFaceDown(c), power(c), toughness(c), c.tapped, c.damage, c.plus_one_counters, c.minus_one_counters, c.loyalty_counters,
    c.lore_counters, c.named_counters, c.is_token, c.chosen, c.doors, c.saddled, c.prepared, c.class_level, c.case_solved, isSummoningSick(state, seat, c),
  ]);
}

// 302.6: a creature can't attack or {T} unless its controller has controlled it continuously since
// the start of their most recent turn.
export function isSummoningSick(state: PublicState, controller: string, c: PublicCard): boolean {
  if (!isCreatureCard(c) || c.characteristics.keywords.some((k) => k.kind === "Haste")) return false;
  return c.entered_battlefield_turn >= mostRecentTurnOf(state, controller);
}

// Turns go round the live players in turn order, one turn number each.
function mostRecentTurnOf(state: PublicState, pid: string): number {
  const live = livePlayers(state);
  const active = live.indexOf(state.active);
  const theirs = live.indexOf(pid);
  if (active < 0 || theirs < 0) return state.turn;
  return state.turn - ((active - theirs + live.length) % live.length);
}

// A permanent on the battlefield shows as a landscape crop of its art, this tall for its width.
export const TILE_RATIO = 0.73;

export type Frame = "W" | "U" | "B" | "R" | "G" | "gold" | "colorless";

const COLOR_OF: { [symbol: string]: "W" | "U" | "B" | "R" | "G" } = { White: "W", Blue: "U", Black: "B", Red: "R", Green: "G" };
const BASIC_COLOR: { [subtype: string]: "W" | "U" | "B" | "R" | "G" } = { Plains: "W", Island: "U", Swamp: "B", Mountain: "R", Forest: "G" };

// The colour of a permanent's frame: its mana cost's colour, gold for two or more, a land's basic
// land types' colour, else colourless.
export function frameOf(c: PublicCard): Frame {
  const colors = new Set<string>();
  const add = (m: ManaSymbol): void => {
    if (m.kind === "Hybrid") { add(m.values[0]); add(m.values[1]); }
    else if (m.kind === "Phyrexian") add(m.values[0]);
    else if (COLOR_OF[m.kind]) colors.add(COLOR_OF[m.kind]!);
  };
  c.characteristics.cost.forEach(add);
  if (colors.size === 0 && isLandCard(c)) for (const t of c.characteristics.subtypes) if (BASIC_COLOR[t]) colors.add(BASIC_COLOR[t]!);
  if (colors.size > 1) return "gold";
  const [only] = colors;
  return (only as Frame | undefined) ?? "colorless";
}
